/**
 * MyFormsInbox
 *
 * Personal queue of form_instances assigned to the current user. Powered by
 * `GET /api/forms/instances/?mine=true` (BE PR #52). Click a row to open the
 * editor; submission flips status away from draft and the row tidies into the
 * completed pile.
 *
 * Surfaces only target_entity='registered_user' templates by default — those
 * are the staff-facing forms Coordinators assign to Promotoras. Users can
 * flip a filter to see *all* instances they're attached to (e.g. legacy
 * member-attached drafts they own).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formsAPI, FormInstance, FormInstanceStatus } from '@/services/api/forms';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { FormInstanceEditor } from './FormInstanceEditorByOrg';
import { useTabbedChrome } from '@/components/weavein';

type Filter = 'open' | 'all' | 'completed';

export const MyFormsInbox: React.FC = () => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [instances, setInstances] = useState<FormInstance[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('open');
  const [openInstanceId, setOpenInstanceId] = useState<number | null>(null);

  const fetchInbox = () => {
    setInstances(null);
    setError(null);
    formsAPI
      .listFormInstances({ mine: true, page_size: 100 })
      .then((rows) => setInstances(rows || []))
      .catch((e: any) => setError(e?.message || t('my_forms.load_failed', 'Failed to load your forms')));
  };

  useEffect(() => {
    fetchInbox();
  }, []);

  const filtered = useMemo(() => {
    if (!instances) return [];
    if (filter === 'open') return instances.filter((i) => i.status === 'draft');
    if (filter === 'completed')
      return instances.filter((i) => i.status === 'submitted' || i.status === 'approved' || i.status === 'reviewed');
    return instances;
  }, [instances, filter]);

  const counts = useMemo(() => {
    const all = instances || [];
    return {
      open: all.filter((i) => i.status === 'draft').length,
      completed: all.filter((i) => i.status !== 'draft').length,
      all: all.length,
    };
  }, [instances]);

  if (openInstanceId != null) {
    return (
      <FormInstanceEditor
        instanceId={openInstanceId}
        onClose={() => {
          setOpenInstanceId(null);
          fetchInbox();
        }}
        onSubmit={() => {
          setOpenInstanceId(null);
          fetchInbox();
        }}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <header>
        {!tabbed && (
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-blue-600" />
          {t('my_forms.title', 'My Forms')}
        </h1>
        )}
        <p className="text-sm text-gray-500 mt-1">
          {t('my_forms.subtitle', 'Forms assigned to you — fill out and submit when ready.')}
        </p>
      </header>

      <div className="flex gap-1 flex-wrap">
        {(['open', 'completed', 'all'] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              filter === f
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white dark:bg-gray-900 text-gray-700 border-gray-200 dark:border-gray-700 hover:bg-gray-50'
            }`}
          >
            {f === 'open'
              ? t('my_forms.filter_open', 'Open ({{count}})', { count: counts.open })
              : f === 'completed'
                ? t('my_forms.filter_completed', 'Completed ({{count}})', { count: counts.completed })
                : t('my_forms.filter_all', 'All ({{count}})', { count: counts.all })}
          </button>
        ))}
        <div className="ml-auto">
          <Button variant="outline" size="sm" onClick={fetchInbox} disabled={instances === null}>
            {t('common.refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-800 inline-flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </CardContent>
        </Card>
      )}

      {instances === null && !error ? (
        <div className="text-sm text-gray-500 py-8 text-center inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', 'Loading…')}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-gray-500">
            {filter === 'open'
              ? t('my_forms.empty_open', 'No open forms assigned to you.')
              : filter === 'completed'
                ? t('my_forms.empty_completed', 'No completed forms yet.')
                : t('my_forms.empty_all', 'No forms assigned to you.')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((inst) => (
            <InboxRow key={inst.id} instance={inst} onOpen={() => setOpenInstanceId(inst.id)} />
          ))}
        </div>
      )}
    </div>
  );
};

const InboxRow: React.FC<{ instance: FormInstance; onOpen: () => void }> = ({ instance, onOpen }) => {
  const { t, language } = useAppTranslation();
  const due = instance.due_date ? new Date(instance.due_date) : null;
  const isOverdue =
    due != null && instance.status === 'draft' && due.getTime() < Date.now();
  // A Spanish caseworker reading "Sep 4, 2026" is the same gap as an English
  // label: pass the app language rather than the browser/OS locale.
  const fmtDue = due
    ? due.toLocaleDateString(language || undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Card>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={onOpen}
          className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
        >
          <div className="flex justify-between items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">
                  {instance.form_template_name || t('my_forms.untitled_form', 'Form #{{id}}', { id: instance.id })}
                </span>
                <StatusBadge status={instance.status} />
                {isOverdue && (
                  <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                    {t('my_forms.overdue', 'Overdue')}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1 flex gap-3 flex-wrap items-center">
                {fmtDue && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t('my_forms.due_on', 'Due {{date}}', { date: fmtDue })}
                  </span>
                )}
                {instance.completion_percentage != null && (
                  <span>
                    {t('my_forms.percent_complete', '{{percent}}% complete', {
                      percent: Math.round(instance.completion_percentage),
                    })}
                  </span>
                )}
                {instance.member_name && (
                  <span>{t('my_forms.about_member', '· About {{name}}', { name: instance.member_name })}</span>
                )}
              </div>
            </div>
            <div className="text-xs text-blue-600 flex-shrink-0 self-center">
              {instance.status === 'draft' ? t('common.open', 'Open') : t('common.view', 'View')}
            </div>
          </div>
        </button>
      </CardContent>
    </Card>
  );
};

const StatusBadge: React.FC<{ status: FormInstanceStatus }> = ({ status }) => {
  const { t } = useAppTranslation();
  if (status === 'draft') {
    return (
      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
        {t('form_status.draft', 'Draft')}
      </Badge>
    );
  }
  if (status === 'submitted') {
    return (
      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 inline-flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> {t('form_status.submitted', 'Submitted')}
      </Badge>
    );
  }
  if (status === 'reviewed') {
    return (
      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
        {t('form_status.reviewed', 'Reviewed')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 inline-flex items-center gap-1">
      <CheckCircle2 className="w-3 h-3" /> {t('form_status.approved', 'Approved')}
    </Badge>
  );
};

export default MyFormsInbox;
